import { ArrowDownIcon, ArrowUpIcon } from '@synergy/theme';
import { Collapse, Icon, List, ListItemButton, ListItemIcon, ListItemText, Theme, Tooltip } from '@mui/material';
import { Component, ReactNode } from 'react';
import { Location } from 'react-router-dom';
import { WithStyles, createStyles, withStyles } from '@mui/styles';

import { NavigationLinkButtonProps } from './navigation-link-button';

const styles = (theme: Theme) =>
    createStyles({
        iconInactive: {
            fontSize: theme.sidebar.iconSize,
            color: theme.sidebar.palette.text,
        },
        iconActive: {
            fontSize: theme.sidebar.iconSize,
            color: theme.sidebar.palette.activeLink,
        },
        textInactive: {
            fontSize: theme.sidebar.linkItemSize,
            color: theme.sidebar.palette.text,
        },
        textActive: {
            fontSize: theme.sidebar.linkItemSize,
            color: theme.sidebar.palette.activeLink,
        },
        guttersClosed: {
            paddingLeft: (theme.sidebar.widthCollapsed - theme.sidebar.iconSize) / 2,
        },
        guttersOpened: {
            paddingLeft: (theme.sidebar.widthCollapsed - theme.sidebar.iconSize) / 2,
        },
        iconBase: {
            marginRight: 0,
            height: 24,
            verticalAlign: 'middle',
        },
        svgIcon: {
            '& > svg': {
                width: 20,
                height: 20,
            },
        },
        hover: {
            '&:hover': {
                backgroundColor: '#2E353D',
            },
        },
    });

interface NestedSectionProps extends WithStyles<typeof styles> {
    icon: ReactNode;
    text: string | ReactNode;
    collapsed: boolean;
    location: Location;
    onClick?: () => void;
    isActive?: boolean;
    initialOpen?: boolean;
    children: ReactNode[] | ReactNode;
    drawerCollapsed?: boolean;
}

interface NestedSectionState {
    isOpen: boolean;
}

export const NestedSection = withStyles(styles)(
    class NestedSectionBase extends Component<NestedSectionProps, NestedSectionState> {
        public override state = {
            isOpen: !!this.props.initialOpen,
        };

        private handleClick = () => {
            if (!this.props.collapsed) {
                this.setState({ isOpen: !this.state.isOpen });
            } else {
                this.setState({ isOpen: true });
            }

            if (this.props.onClick) {
                this.props.onClick();
            }
        };

        private isActive = () => {
            const searchArray = Array.isArray(this.props.children) ? this.props.children : [this.props.children];
            for (const c of searchArray) {
                const url = this.findUrlProp(c);
                if (url) {
                    if (this.props.location.pathname.startsWith(url)) {
                        return true;
                    }
                }
            }
            return false;
        };

        private findUrlProp = (c: unknown, depth = 0): string | null => {
            if (depth > 3) {
                return null;
            }

            const button = c as Component<NavigationLinkButtonProps>;
            if (button?.props?.url) {
                return button?.props?.url;
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return this.findUrlProp((c as any)?.props?.children, depth + 1);
        };

        public override render() {
            const { icon, text, collapsed, children, classes, drawerCollapsed } = this.props;
            const isOpen = this.state.isOpen && !collapsed;
            const iconStyle = this.props.isActive || this.isActive() ? classes.iconActive : classes.iconInactive;
            const textStyle = this.props.isActive || this.isActive() ? classes.textActive : classes.textInactive;

            let renderIcon = null;
            if (typeof icon === 'string') {
                renderIcon = <Icon component="div" classes={{ root: iconStyle }} className={icon} />;
            } else {
                renderIcon = <div className={`${iconStyle} ${classes.svgIcon}`}>{icon}</div>;
            }

            return (
                <Tooltip title={(drawerCollapsed && text) || ''} placement="right">
                    <div>
                        <ListItemButton
                            onClick={this.handleClick}
                            classes={{
                                root: classes.hover,
                                gutters: drawerCollapsed ? classes.guttersClosed : classes.guttersOpened,
                            }}
                            disableGutters={false}
                        >
                            <ListItemIcon className={classes.iconBase}>{renderIcon}</ListItemIcon>
                            <ListItemText primary={text} classes={{ primary: textStyle }} />
                            {isOpen ? <ArrowDownIcon /> : <ArrowUpIcon />}
                        </ListItemButton>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit={true}>
                            <List component="div" disablePadding={true}>
                                {children}
                            </List>
                        </Collapse>
                    </div>
                </Tooltip>
            );
        }
    }
);
